import DOMPurify from 'dompurify';

// Input validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[\d\s()-]{10,20}$/;
  return phoneRegex.test(phone.trim());
};

export const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z\s\u00C0-\u024F\u1E00-\u1EFF]{2,50}$/;
  return nameRegex.test(name.trim());
};

export const validateCountry = (country: string): boolean => {
  const countryRegex = /^[a-zA-Z\s\u00C0-\u024F\u1E00-\u1EFF]{2,50}$/;
  return countryRegex.test(country.trim());
};

// Sanitization utilities
export const sanitizeText = (text: string): string => {
  return text.trim().replace(/[<>'"&]/g, '');
};

export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
    ALLOWED_ATTR: []
  });
};

// Enhanced rate limiting check (client-side)
export const checkRateLimit = (key: string, maxAttempts: number, timeWindow: number): boolean => {
  const now = Date.now();
  const attempts = JSON.parse(localStorage.getItem(`rate_${key}`) || '[]') as number[];
  
  // Remove expired attempts
  const validAttempts = attempts.filter(timestamp => now - timestamp < timeWindow);
  
  if (validAttempts.length >= maxAttempts) {
    return false; // Rate limit exceeded
  }
  
  // Add current attempt with progressive delay tracking
  validAttempts.push(now);
  localStorage.setItem(`rate_${key}`, JSON.stringify(validAttempts));
  
  return true; // Within rate limit
};

// Calculate progressive delay for repeated failures
export const calculateProgressiveDelay = (attemptCount: number): number => {
  if (attemptCount <= 3) return 0;
  if (attemptCount <= 5) return 5000; // 5 seconds
  if (attemptCount <= 10) return 30000; // 30 seconds
  return 300000; // 5 minutes
};

// Security event logging utility
export const logSecurityEvent = (event: string, details: Record<string, any> = {}) => {
  if (typeof window !== 'undefined') {
    console.warn(`[SECURITY EVENT] ${event}:`, details);
    // In production, send to monitoring service
  }
};

// Enhanced security headers for edge functions
export const securityHeaders = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:;",
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=()'
};

// Production-ready CORS headers
export const corsHeaders = {
  'Access-Control-Allow-Origin': process.env.NODE_ENV === 'production' 
    ? 'https://your-domain.com' // Replace with your actual domain
    : '*',
  'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type',
  'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
  'Access-Control-Max-Age': '86400',
  'Access-Control-Allow-Credentials': 'true'
};